import { useCustomKpis } from 'common/queries/customKpis';
import { useEnsureAllKpis, useGetAllKpis } from 'common/queries/kpis';
import { CustomKpi } from 'common/types/customKpi';
import { KPI, KPIGroups } from 'common/types/kpi';
import { useCallback, useMemo } from 'react';
import { createCustomKpiLabelMap, createCustomKpisMap, createKpiMap, groupKpisByType, sortKPIs } from 'utils/kpi';

export const useKpis = (studyId: string): KPI[] => {
  const { data: kpis } = useGetAllKpis();

  return useMemo(() => {
    const studySpecificKpi = kpis?.find(kpi => kpi.study_uuid === studyId);
    return sortKPIs(studySpecificKpi?.kpis || []);
  }, [kpis, studyId]);
};

export const useKpisList = (): ((studyId: string) => KPI[]) => {
  const { data: kpis } = useGetAllKpis();

  const getKpiList = useCallback(
    (studyId: string) => {
      const studySpecificKpi = kpis?.find(kpi => kpi.study_uuid === studyId);
      return sortKPIs(studySpecificKpi?.kpis || []);
    },
    [kpis],
  );

  return getKpiList;
};

// Unlike useKpisList, waits for the shared allKpis query instead of reading its current
// (possibly not yet loaded) cache — for imperative flows that must not race app startup
export const useEnsureKpisList = (): ((studyId: string) => Promise<KPI[]>) => {
  const ensureAllKpis = useEnsureAllKpis();

  const getKpiList = useCallback(
    async (studyId: string) => {
      const kpis = await ensureAllKpis();
      const studySpecificKpi = kpis?.find(kpi => kpi.study_uuid === studyId);
      return sortKPIs(studySpecificKpi?.kpis || []);
    },
    [ensureAllKpis],
  );

  return getKpiList;
};

const NO_CUSTOM_KPIS: CustomKpi[] = [];

// Custom KPIs of the study, keyed by the identifier charts send in kpi_option_identifiers.
// Look identifiers up here; never infer a custom KPI from the identifier's format.
export const useCustomKpisMap = (studyId: string): Record<string, CustomKpi> => {
  const { data: customKpis = NO_CUSTOM_KPIS } = useCustomKpis(studyId);

  return useMemo(() => createCustomKpisMap(customKpis), [customKpis]);
};

export const useAllKpisMap = (studyId: string): Record<string, string> => {
  const kpis = useKpis(studyId);
  const { data: customKpis = NO_CUSTOM_KPIS } = useCustomKpis(studyId);

  return useMemo(() => {
    return { ...createKpiMap(kpis), ...createCustomKpiLabelMap(customKpis) };
  }, [kpis, customKpis]);
};

// Custom KPIs are percentage-based (SRS A2), so selecting one deselects absolute KPIs and vice versa.
export const useKpiTypeGroups = (studyId: string): KPIGroups => {
  const kpis = useKpis(studyId);
  const { data: customKpis = NO_CUSTOM_KPIS } = useCustomKpis(studyId);

  return useMemo(() => {
    const groups = groupKpisByType(kpis);
    return { ...groups, percentage: [...groups.percentage, ...customKpis.map(({ identifier }) => identifier)] };
  }, [kpis, customKpis]);
};
